/**
 * Phase 9.6c: Project Creation Wizard
 *
 * A 4- or 5-step wizard for creating new projects/proposals:
 * 1. Category Selection
 * 2. Type Selection (Proposal vs Project)
 * 3. Details (Name, Client)
 * 4. Renewal (only for renewal-enabled categories)
 * 5. Review & Submit
 */
import { useState, useRef } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useProjectCategoryOptions, useCreateProjectCategoryOption } from '@/hooks/useProjectCategories';
import { toast } from 'sonner';
import { useCreateProject, useNextProjectNumberPreview } from '@/hooks/useProjects';
import { usePermitTypes } from '@/hooks/usePermits';
import type { PermitTypeDefinition } from '@/types/permits';
import { RenewalFields } from '@/components/RenewalFields';
import { formatExpiryDate, renewalNotSavedMessage } from '@/lib/renewalResult';
import { Plus, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { SearchableOrganizationCombobox } from '@/components/organizations/SearchableOrganizationCombobox';
import { OrganizationFormModal } from '@/components/forms/OrganizationFormModal';

// ============================================================================
// Types & Schemas
// ============================================================================

type ProjectType = 'proposal' | 'project';

interface WizardStep {
  id: string;
  label: string;
}

const BASE_STEPS: WizardStep[] = [
  { id: 'category', label: 'Category' },
  { id: 'type',     label: 'Type' },
  { id: 'details',  label: 'Details' },
  { id: 'review',   label: 'Review' },
];

const RENEWAL_STEPS: WizardStep[] = [
  { id: 'category', label: 'Category' },
  { id: 'renewal',  label: 'Renewal' },
  { id: 'type',     label: 'Type' },
  { id: 'details',  label: 'Details' },
  { id: 'review',   label: 'Review' },
];

interface WizardData {
  categoryId: string;
  categoryName: string;
  type: ProjectType;
  name: string;
  organizationId?: string;
  organizationName?: string; // 9.21h: Store name to avoid lookup issues
  isRenewal: boolean;
  renewalExpiryDate: string;
  renewalRecurringPeriod: string;
  renewalPermitTypeDefinitionId: string;
  renewalPermitTypeName: string;
  renewalReminderDays: number[];
}

const detailsSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  organizationId: z.string().optional(),
});

// ============================================================================
// Progress Indicator
// ============================================================================

interface WizardProgressProps {
  currentStep: number;
  steps: WizardStep[];
}

function WizardProgress({ currentStep, steps }: WizardProgressProps) {
  return (
    <nav aria-label="Progress">
      <ol role="list" className="flex items-center justify-center space-x-4 mb-8">
        {steps.map((step, index) => {
          const isComplete = index < currentStep;
          const isCurrent = index === currentStep;

          return (
            <li key={step.id} className="flex items-center">
              <div
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium',
                  isComplete && 'bg-primary text-primary-foreground',
                  isCurrent && 'border-2 border-primary text-primary',
                  !isComplete && !isCurrent && 'border border-muted-foreground/30 text-muted-foreground'
                )}
              >
                {isComplete ? (
                  <Check className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>
              <span className={cn(
                'ml-2 text-sm font-medium hidden sm:block',
                isCurrent && 'text-primary',
                !isCurrent && 'text-muted-foreground'
              )}>
                {step.label}
              </span>
              {index < steps.length - 1 && (
                <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground/50" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ============================================================================
// Step 1: Category Selection
// ============================================================================

interface CategoryStepProps {
  categories: ReturnType<typeof useProjectCategoryOptions>['data'];
  isLoading: boolean;
  selectedCategoryId: string | undefined;
  onSelect: (categoryId: string, categoryName: string) => void;
  onAddCategory: () => void;
}

function CategoryStep({ categories, isLoading, selectedCategoryId, onSelect, onAddCategory }: CategoryStepProps) {
  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading categories...</div>;
  }

  return (
    <div data-testid="wizard-step-category" className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold">What type of project is this?</h2>
        <p className="text-muted-foreground mt-1">Select a category to continue</p>
      </div>

      <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
        {categories?.map((category) => (
          <button
            key={category.id}
            onClick={() => onSelect(category.id, category.value)}
            className={cn(
              'p-4 rounded-lg border text-left transition-all',
              selectedCategoryId === category.id
                ? 'border-primary bg-primary/5 ring-2 ring-primary'
                : 'border-border hover:border-primary/50 hover:bg-muted/50'
            )}
          >
            <div className="font-medium">{category.value}</div>
            {category.number_prefix && (
              <div className="text-sm text-muted-foreground mt-1">
                Prefix: {category.number_prefix}
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="text-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={onAddCategory}
          className="text-primary"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add New Category
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Step 2: Type Selection
// ============================================================================

interface TypeStepProps {
  selectedType: ProjectType;
  onSelect: (type: ProjectType) => void;
  categoryId: string | undefined;
}

function TypeStep({ selectedType, onSelect, categoryId }: TypeStepProps) {
  const { data: numberPreview, isLoading: isLoadingNumber } = useNextProjectNumberPreview(categoryId);

  return (
    <div data-testid="wizard-step-type" className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold">Is this a proposal or active project?</h2>
        <p className="text-muted-foreground mt-1">Select the type to continue</p>
      </div>

      <RadioGroup
        value={selectedType}
        onValueChange={(value) => onSelect(value as ProjectType)}
        className="max-w-md mx-auto space-y-4"
      >
        {/* v0.12.27 Item 2: wrap whole card in <label> so clicking anywhere selects */}
        <label
          htmlFor="proposal"
          className="flex items-start space-x-3 p-4 rounded-lg border hover:bg-muted/50 cursor-pointer"
          data-testid="wizard-type-card-proposal"
        >
          <RadioGroupItem value="proposal" id="proposal" className="mt-1" />
          <span className="flex-1">
            <span className="block font-medium">Proposal</span>
            <span className="block text-sm text-muted-foreground">
              A proposal you're sending to a potential client. Won't be assigned a project number until accepted.
            </span>
          </span>
        </label>

        <label
          htmlFor="project"
          className="flex items-start space-x-3 p-4 rounded-lg border hover:bg-muted/50 cursor-pointer"
          data-testid="wizard-type-card-project"
        >
          <RadioGroupItem value="project" id="project" className="mt-1" />
          <span className="flex-1">
            <span className="block font-medium">Project</span>
            <span className="block text-sm text-muted-foreground">
              An active project that's already been confirmed. Will be assigned a project number immediately.
            </span>
            {selectedType === 'project' && numberPreview && (
              <span className="mt-2 p-2 bg-primary/10 rounded text-sm block">
                This project will be assigned number: <span className="font-mono font-semibold">{numberPreview}</span>
              </span>
            )}
          </span>
        </label>
      </RadioGroup>
    </div>
  );
}

// ============================================================================
// Step 3: Details
// ============================================================================

// ----------------------------------------------------------------------------
// R3 Client-required matrix
// See: ai-orchestration/metabus/planning/SPEC-keystone-client-required-per-category.md
// Decision #4: proposal flow uses softer friction than project flow.
// Encoded as a single config object (not nested conditionals) so future editors
// can't silently flatten the proposal/project distinction.
// ----------------------------------------------------------------------------
type WizardFlow = 'proposal_draft' | 'project_setup';

interface ClientRequirementConfig {
  label: string;            // Client field label
  disableNextOnEmpty: boolean; // Hard-gate next when empty
  inlineWarning: string | null; // Soft warning shown inline
  confirmOnEmpty: boolean;  // Show "are you sure?" dialog on submit when empty
  allowDeferred: boolean;   // Pass allow_client_deferred=true to backend
}

export const CLIENT_REQUIREMENT_MATRIX: Record<string, ClientRequirementConfig> = {
  // client_required=TRUE + project_setup → hard gate
  'true:project_setup': {
    label: 'Client *',
    disableNextOnEmpty: true,
    inlineWarning: null,
    confirmOnEmpty: false,
    allowDeferred: false,
  },
  // client_required=TRUE + proposal_draft → soft warning, allow deferred
  'true:proposal_draft': {
    label: 'Client (recommended)',
    disableNextOnEmpty: false,
    inlineWarning: 'No client yet — you can add one later, but this category normally requires one.',
    confirmOnEmpty: false,
    allowDeferred: true,
  },
  // client_required=FALSE + project_setup → confirm dialog
  'false:project_setup': {
    label: 'Client (optional)',
    disableNextOnEmpty: false,
    inlineWarning: null,
    confirmOnEmpty: true,
    allowDeferred: false,
  },
  // client_required=FALSE + proposal_draft → silent
  'false:proposal_draft': {
    label: 'Client (optional)',
    disableNextOnEmpty: false,
    inlineWarning: null,
    confirmOnEmpty: false,
    allowDeferred: false,
  },
};

export function getClientRequirementConfig(
  clientRequired: boolean,
  flow: WizardFlow
): ClientRequirementConfig {
  return CLIENT_REQUIREMENT_MATRIX[`${clientRequired}:${flow}`];
}

interface DetailsStepProps {
  name: string;
  organizationId?: string;
  // v0.12.27 Item 5: optional site address captured at project creation
  address: string;
  onNameChange: (name: string) => void;
  onOrganizationChange: (orgId: string | undefined) => void;
  onOrganizationSelect: (orgId: string, orgName: string) => void; // 9.21h: Receive both id and name
  onCreateOrganization: () => void;
  onAddressChange: (address: string) => void;
  error?: string;
  clientConfig: ClientRequirementConfig;
  clientComboboxRef?: React.RefObject<HTMLDivElement>;
}

function DetailsStep({ name, organizationId, address, onNameChange, onOrganizationChange, onOrganizationSelect, onCreateOrganization, onAddressChange, error, clientConfig, clientComboboxRef }: DetailsStepProps) {
  const showWarning = !organizationId && !!clientConfig.inlineWarning;

  return (
    <div data-testid="wizard-step-details" className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold">Project Details</h2>
        <p className="text-muted-foreground mt-1">Enter the basic information</p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        <div className="space-y-2">
          <Label htmlFor="project-name">Project Name *</Label>
          <Input
            id="project-name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Enter project name"
            className={cn(error && 'border-destructive')}
          />
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <div className="space-y-2" ref={clientComboboxRef}>
          <Label>{clientConfig.label}</Label>
          <SearchableOrganizationCombobox
            value={organizationId}
            onValueChange={(value) => onOrganizationChange(value || undefined)}
            onSelect={onOrganizationSelect} // 9.21h: Also capture name on selection
            role="client"
            useServerSearch={true}
            onCreateNew={onCreateOrganization}
            placeholder="Select client"
          />
          {showWarning && (
            <p className="text-sm text-amber-600" data-testid="client-inline-warning">
              {clientConfig.inlineWarning}
            </p>
          )}
        </div>

        {/* v0.12.27 Item 5/6: optional Site Address captured at creation */}
        <div className="space-y-2">
          <Label htmlFor="project-site-address">Site Address <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <textarea
            id="project-site-address"
            data-testid="wizard-site-address"
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
            placeholder="e.g., 379 Adelaide St West, Toronto ON M5V 1S5"
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <p className="text-xs text-muted-foreground">
            The project's physical location (used on proposal PDFs, draw certificates, and weather lookups). Different from the client's billing address.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 4: Renewal (only shown for renewal-enabled categories)
// ============================================================================

interface RenewalStepProps {
  isRenewal: boolean;
  onIsRenewalChange: (v: boolean) => void;
  permitTypeDefinitions: PermitTypeDefinition[];
  permitTypeDefinitionId: string;
  onPermitTypeDefinitionIdChange: (id: string) => void;
  expiryDate: string;
  onExpiryDateChange: (v: string) => void;
  reminderDays: number[];
  onReminderDaysChange: (v: number[]) => void;
  recurringPeriod: string;
  onRecurringPeriodChange: (v: string) => void;
}

function RenewalStep({
  isRenewal,
  onIsRenewalChange,
  permitTypeDefinitions,
  permitTypeDefinitionId,
  onPermitTypeDefinitionIdChange,
  expiryDate,
  onExpiryDateChange,
  reminderDays,
  onReminderDaysChange,
  recurringPeriod,
  onRecurringPeriodChange,
}: RenewalStepProps) {
  return (
    <div data-testid="wizard-step-renewal" className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold">Is this project a renewal?</h2>
        <p className="text-muted-foreground mt-1">Track expiry dates and get reminders automatically</p>
      </div>

      <div className="max-w-md mx-auto">
        {/* Yes/No toggle */}
        <div className="flex gap-3 justify-center mb-6">
          <button
            type="button"
            onClick={() => onIsRenewalChange(false)}
            className={cn(
              'px-8 py-2 rounded-lg border font-medium transition-all',
              !isRenewal
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border hover:border-primary/50 hover:bg-muted/50'
            )}
          >
            No
          </button>
          <button
            type="button"
            onClick={() => onIsRenewalChange(true)}
            className={cn(
              'px-8 py-2 rounded-lg border font-medium transition-all',
              isRenewal
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border hover:border-primary/50 hover:bg-muted/50'
            )}
          >
            Yes
          </button>
        </div>

        {isRenewal && (
          <div className="p-4 rounded-lg border bg-muted/30">
            <RenewalFields
              permitTypeDefinitions={permitTypeDefinitions}
              permitTypeDefinitionId={permitTypeDefinitionId}
              onPermitTypeDefinitionIdChange={onPermitTypeDefinitionIdChange}
              expiryDate={expiryDate}
              onExpiryDateChange={onExpiryDateChange}
              recurringPeriod={recurringPeriod}
              onRecurringPeriodChange={onRecurringPeriodChange}
              reminderDays={reminderDays}
              onReminderDaysChange={onReminderDaysChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Step 5: Review
// ============================================================================

interface ReviewStepProps {
  data: WizardData;
}

function ReviewStep({ data }: ReviewStepProps) {
  // 9.21h: Use stored organizationName directly instead of looking up in organizations list
  // This fixes the blank client issue when org isn't in the initial query results
  const clientName = data.organizationName || (data.organizationId ? 'Loading...' : 'None selected');

  return (
    <div data-testid="wizard-step-review" className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold">Review & Create</h2>
        <p className="text-muted-foreground mt-1">Confirm your selections</p>
      </div>

      <div className="max-w-md mx-auto space-y-4 bg-muted/50 rounded-lg p-6">
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Category</span>
          <span className="font-medium">{data.categoryName}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Type</span>
          <span className="font-medium capitalize">{data.type}</span>
        </div>
        <div className="flex justify-between py-2 border-b">
          <span className="text-muted-foreground">Name</span>
          <span className="font-medium">{data.name}</span>
        </div>
        <div className={cn('flex justify-between py-2', data.isRenewal ? 'border-b' : '')}>
          <span className="text-muted-foreground">Client</span>
          <span className="font-medium">{clientName}</span>
        </div>

        {/* Renewal summary */}
        {data.isRenewal && data.renewalExpiryDate && (
          <>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Renewal Tracking</span>
              <span className="font-medium text-green-600">Active</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Expires</span>
              <span className="font-medium">{data.renewalExpiryDate}</span>
            </div>
            {data.renewalRecurringPeriod && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Renews</span>
                <span className="font-medium">{data.renewalRecurringPeriod}</span>
              </div>
            )}
            {data.renewalPermitTypeName && (
              <div className="flex justify-between py-2">
                <span className="text-muted-foreground">Permit Type</span>
                <span className="font-medium">{data.renewalPermitTypeName}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Create Category Dialog
// ============================================================================

interface CreateCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (categoryId: string, categoryName: string) => void;
}

function CreateCategoryDialog({ open, onOpenChange, onCreated }: CreateCategoryDialogProps) {
  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const createCategory = useCreateProjectCategoryOption();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      const result = await createCategory.mutateAsync({
        value: name.trim(),
        number_prefix: prefix.trim() || undefined,
      });
      onCreated(result.id, result.value);
      setName('');
      setPrefix('');
      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Category</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category-name">Category Name</Label>
            <Input
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Commercial"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category-prefix">Number Prefix (optional)</Label>
            <Input
              id="category-prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              placeholder="e.g., C"
              maxLength={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || createCategory.isPending}>
              {createCategory.isPending ? 'Creating...' : 'Create Category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Main Wizard Content Component (exported for testing without DashboardLayout)
// ============================================================================

export function ProjectWizardContent() {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const { data: permitTypesData } = usePermitTypes();
  const permitTypeDefinitions = permitTypesData?.permit_types?.filter(t => t.is_active) ?? [];
  // Category data (lifted here so we can read renewal config on selection)
  const { data: categories, isLoading: categoriesLoading } = useProjectCategoryOptions();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [categoryId, setCategoryId] = useState<string>();
  const [categoryName, setCategoryName] = useState<string>('');
  const [projectType, setProjectType] = useState<ProjectType>('proposal');
  const [projectName, setProjectName] = useState('');
  const [organizationId, setOrganizationId] = useState<string>();
  const [organizationName, setOrganizationName] = useState<string>(); // 9.21h: Store name to avoid lookup
  // v0.12.27 Item 5: optional site address captured at creation
  const [siteAddress, setSiteAddress] = useState<string>('');
  const [nameError, setNameError] = useState<string>();
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showCreateOrganization, setShowCreateOrganization] = useState(false);

  // R3: client_required per-category flag (Spec keystone-client-required-per-category)
  const [categoryClientRequired, setCategoryClientRequired] = useState(false);
  const [showEmptyClientConfirm, setShowEmptyClientConfirm] = useState(false);
  const clientComboboxRef = useRef<HTMLDivElement>(null);

  // Renewal state
  const [categoryRenewalEnabled, setCategoryRenewalEnabled] = useState(false);
  const [isRenewal, setIsRenewal] = useState(false);
  const [renewalExpiryDate, setRenewalExpiryDate] = useState('');
  const [renewalReminderDays, setRenewalReminderDays] = useState<number[]>([30, 14]);
  const [renewalRecurringPeriod, setRenewalRecurringPeriod] = useState('');
  const [renewalPermitTypeDefinitionId, setRenewalPermitTypeDefinitionId] = useState('');
  const [renewalPermitTypeName, setRenewalPermitTypeName] = useState('');

  // Active steps depend on whether selected category has renewal tracking
  const activeSteps = categoryRenewalEnabled ? RENEWAL_STEPS : BASE_STEPS;

  // R3: Derive flow + client-requirement config
  const flow: WizardFlow = projectType === 'proposal' ? 'proposal_draft' : 'project_setup';
  const clientConfig = getClientRequirementConfig(categoryClientRequired, flow);

  // Navigation
  const canGoBack = currentStep > 0;
  const canGoNext = () => {
    const stepId = activeSteps[currentStep]?.id;
    switch (stepId) {
      case 'category': return !!categoryId;
      case 'type':     return true;
      case 'details':
        if (projectName.trim().length === 0) return false;
        // R3: strict project_setup + client_required=TRUE requires an organization
        if (clientConfig.disableNextOnEmpty && !organizationId) return false;
        return true;
      case 'renewal':
        if (!isRenewal) return true; // "No" always valid
        return renewalExpiryDate.length > 0; // "Yes" requires expiry date
      case 'review':   return true;
      default:         return false;
    }
  };

  const handleNext = () => {
    if (activeSteps[currentStep]?.id === 'details') {
      if (!projectName.trim()) {
        setNameError('Name is required');
        return;
      }
      setNameError(undefined);
    }

    if (currentStep < activeSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const doSubmit = async () => {
    if (!categoryId || !categoryName || !projectName.trim()) return;

    const renewalData = (categoryRenewalEnabled && isRenewal && renewalExpiryDate)
      ? {
          // v0.17.16: blank means "none", never "" (an empty string is not a
          // valid permit type id and used to make the renewal fail to save).
          permit_type_definition_id: renewalPermitTypeDefinitionId || null,
          expiry_date: renewalExpiryDate,
          recurring_period: renewalRecurringPeriod || null,
          reminder_days: renewalReminderDays,
        }
      : null;

    try {
      const result = await createProject.mutateAsync({
        name: projectName.trim(),
        category: categoryName,
        workflow_stage: projectType === 'proposal' ? 'proposal_draft' : 'project_setup',
        organization_id: organizationId,
        // v0.12.27 Item 5: forward optional site address to backend
        address: siteAddress.trim() ? siteAddress.trim() : undefined,
        // R3: Soft gate for proposals (backend R2c enforcement)
        allow_client_deferred: clientConfig.allowDeferred,
        renewal_data: renewalData,
      } as any);

      if (result.permit_created) {
        toast.success(`Renewal reminder set for ${formatExpiryDate(renewalExpiryDate)}.`);
      }
      // v0.17.16: the project saved but its renewal did not. Say so, and keep
      // the message up until it is dismissed.
      const renewalWarning = renewalNotSavedMessage(!!renewalData, result);
      if (renewalWarning) {
        toast.warning(renewalWarning, { duration: Infinity });
      }

      navigate(`/projects/${result.project.id}`);
    } catch (error) {
      // createProject failed — error toast shown by mutation hook
    }
  };

  const handleSubmit = async () => {
    // R3: permissive project_setup with empty client → confirm dialog
    if (clientConfig.confirmOnEmpty && !organizationId) {
      setShowEmptyClientConfirm(true);
      return;
    }
    await doSubmit();
  };

  const handleCategorySelect = (id: string, name: string) => {
    setCategoryId(id);
    setCategoryName(name);

    // Look up renewal config from loaded categories
    const cat = categories?.find(c => c.id === id);
    const renewalEnabled = cat?.renewal_tracking_enabled ?? false;
    setCategoryRenewalEnabled(renewalEnabled);
    // R3: capture client_required (defaults to false if backend pre-R2)
    setCategoryClientRequired((cat as any)?.client_required ?? false);

    if (renewalEnabled) {
      // Pre-fill defaults from category settings
      setRenewalRecurringPeriod(cat?.default_recurring_period ?? '');
      // default_reminder_days is now an array (V129)
      if (Array.isArray(cat?.default_reminder_days) && cat.default_reminder_days.length > 0) {
        setRenewalReminderDays(cat.default_reminder_days);
      } else {
        setRenewalReminderDays([30, 14]);
      }
    }

    // Reset renewal answer when category changes
    setIsRenewal(false);
    setRenewalExpiryDate('');
    setRenewalPermitTypeDefinitionId('');
    setRenewalPermitTypeName('');
  };

  const handleCategoryCreated = (id: string, name: string) => {
    // Newly created categories never have renewal tracking enabled
    setCategoryId(id);
    setCategoryName(name);
    setCategoryRenewalEnabled(false);
    // R3: newly created categories default to client_required=false
    setCategoryClientRequired(false);
  };

  // Render current step
  const renderStep = () => {
    const stepId = activeSteps[currentStep]?.id;
    switch (stepId) {
      case 'category':
        return (
          <CategoryStep
            categories={categories}
            isLoading={categoriesLoading}
            selectedCategoryId={categoryId}
            onSelect={handleCategorySelect}
            onAddCategory={() => setShowCreateCategory(true)}
          />
        );
      case 'type':
        return (
          <TypeStep
            selectedType={projectType}
            onSelect={setProjectType}
            categoryId={categoryId}
          />
        );
      case 'details':
        return (
          <DetailsStep
            name={projectName}
            organizationId={organizationId}
            address={siteAddress}
            onNameChange={(name) => {
              setProjectName(name);
              if (name.trim()) setNameError(undefined);
            }}
            onOrganizationChange={setOrganizationId}
            onOrganizationSelect={(id, name) => { // 9.21h: Capture both id and name
              setOrganizationId(id);
              setOrganizationName(name);
            }}
            onCreateOrganization={() => setShowCreateOrganization(true)}
            onAddressChange={setSiteAddress}
            error={nameError}
            clientConfig={clientConfig}
            clientComboboxRef={clientComboboxRef}
          />
        );
      case 'renewal':
        return (
          <RenewalStep
            isRenewal={isRenewal}
            onIsRenewalChange={setIsRenewal}
            permitTypeDefinitions={permitTypeDefinitions}
            permitTypeDefinitionId={renewalPermitTypeDefinitionId}
            onPermitTypeDefinitionIdChange={(id) => {
              setRenewalPermitTypeDefinitionId(id);
              const def = permitTypeDefinitions.find(t => t.id === id);
              setRenewalPermitTypeName(def?.name ?? '');
            }}
            expiryDate={renewalExpiryDate}
            onExpiryDateChange={setRenewalExpiryDate}
            reminderDays={renewalReminderDays}
            onReminderDaysChange={setRenewalReminderDays}
            recurringPeriod={renewalRecurringPeriod}
            onRecurringPeriodChange={setRenewalRecurringPeriod}
          />
        );
      case 'review':
        return (
          <ReviewStep
            data={{
              categoryId: categoryId!,
              categoryName,
              type: projectType,
              name: projectName,
              organizationId,
              organizationName, // 9.21h: Pass stored name to avoid lookup issues
              isRenewal,
              renewalExpiryDate,
              renewalRecurringPeriod,
              renewalPermitTypeDefinitionId,
              renewalPermitTypeName,
              renewalReminderDays,
            }}
          />
        );
      default:
        return null;
    }
  };

  const isSubmitting = createProject.isPending;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <WizardProgress currentStep={currentStep} steps={activeSteps} />

      <div className="min-h-[400px]">
        {renderStep()}
      </div>

      <div className="flex justify-between mt-8 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={!canGoBack}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        {currentStep < activeSteps.length - 1 ? (
          <Button onClick={handleNext} disabled={!canGoNext()}>
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        )}
      </div>

      <CreateCategoryDialog
        open={showCreateCategory}
        onOpenChange={setShowCreateCategory}
        onCreated={handleCategoryCreated}
      />

      <OrganizationFormModal
        open={showCreateOrganization}
        onOpenChange={setShowCreateOrganization}
        fieldSet="minimal"
        onSave={(org) => {
          setOrganizationId(org.id);
          setOrganizationName(org.name); // 9.21h: Also store name for newly created orgs
        }}
      />

      {/* R3: Confirm dialog when submitting permissive project with no client */}
      <AlertDialog open={showEmptyClientConfirm} onOpenChange={setShowEmptyClientConfirm}>
        <AlertDialogContent data-testid="empty-client-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>No client assigned</AlertDialogTitle>
            <AlertDialogDescription>
              This project has no client assigned. You can add one later, but invoicing will be blocked until you do. Continue anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowEmptyClientConfirm(false);
                // Jump focus back to Client combobox (best effort)
                const input = clientComboboxRef.current?.querySelector('input, button');
                if (input instanceof HTMLElement) input.focus();
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowEmptyClientConfirm(false);
                void doSubmit();
              }}
              data-testid="confirm-continue-anyway"
            >
              Continue anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// Main Wizard Component (with DashboardLayout for page use)
// ============================================================================

export function ProjectWizard() {
  return (
    <DashboardLayout title="New Project">
      <ProjectWizardContent />
    </DashboardLayout>
  );
}
